import { Component, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { UpperCasePipe } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideArrowRight } from '@ng-icons/lucide';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {
  I18NEXT_LNG_STORAGE_KEY,
  PREFERRED_LANG_COOKIE_NAME,
  PREFERRED_LANG_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
  isSupportedLanguage,
} from '../../core/i18n/language.constants';
import { TermlyService } from 'src/app/core/termly/termly.service';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { PolicyIframeDialogComponent } from './policy-iframe-dialog.component';

const TERMLY_PRIVACY_URL =
  'https://app.termly.io/policy-viewer/policy.html?policyUUID=331d7d15-6b1f-4396-80dc-8fed2d9a83c2';
const TERMLY_TERMS_URL =
  'https://app.termly.io/policy-viewer/policy.html?policyUUID=ced3eab4-645a-482b-8ff1-94284d307c80';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [
    RouterLink,
    FormsModule,
    HlmButtonImports,
    NgIcon,
    HlmIconImports,
    HlmTypographyImports,
    TranslocoDirective,
    UpperCasePipe,
    BrnSelectImports,
    HlmSelectImports,
  ],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.css',
  providers: [
    provideIcons({
      lucideArrowRight,
    }),
  ],
})
export class FooterComponent {
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly termlyService = inject(TermlyService);
  private readonly dialogService = inject(HlmDialogService);
  protected currentYear = new Date().getFullYear();
  protected readonly languages = SUPPORTED_LANGUAGES;

  protected get activeLang(): SupportedLanguage {
    const lang = this.transloco.getActiveLang() ?? '';
    const normalized = lang.split('-')[0];
    return isSupportedLanguage(normalized) ? normalized : 'en';
  }

  protected switchLanguage(newLang: SupportedLanguage): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(PREFERRED_LANG_STORAGE_KEY, newLang);
      localStorage.setItem(I18NEXT_LNG_STORAGE_KEY, newLang);
      document.cookie = `${PREFERRED_LANG_COOKIE_NAME}=${newLang};path=/;max-age=31536000;samesite=lax`;
    }
    const url = this.router.url;
    const segments = url.split('/').filter(Boolean);
    segments[0] = newLang;
    this.router.navigate(['/' + segments.join('/')]);
  }

  protected showTermlyPreferencesModal() {
    this.termlyService.showPreferencesModal();
  }

  protected openPolicyDialog(url: string) {
    this.dialogService.open(PolicyIframeDialogComponent, {
      context: { url },
      contentClass: 'sm:max-w-4xl w-4xl w-full p-2',
    });
  }

  protected readonly privacyPolicyUrl = TERMLY_PRIVACY_URL;
  protected readonly termsUrl = TERMLY_TERMS_URL;
}
